import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  getFireSuppresantList,
  addFireSuppresantToBusiness,
  updateFireSuppresant,
  deleteFireSuppresant,
} from "../../../../../../apis/Business/FireSuppresantApi";
import { getSuppresantIndustrialData } from "../../../../../../apis/Dropdown";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";

const SuppresantTable = ({ businessId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [dropdownGasType, setDropdownGasType] = useState([]);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    getFireSuppresantList({
      businessId,
      setObjects: setData,
    });
    getSuppresantIndustrialData({ setDropdown: setDropdownGasType });
  }, [businessId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEdit = useCallback(
    (editedItem) => {
      updateFireSuppresant({
        businessId,
        objectId: editedItem.id,
        ...editedItem,
        gas_type: editedItem["gas_type.value"]
          ? editedItem["gas_type.value"].value
          : editedItem["gas_type"]
          ? editedItem["gas_type"].value
          : null,
        gas_used: null,
        objects: data,
        setObjects: setData,
        use_swal: true,
      });
    },
    [businessId, data, setData]
  );

  const handleAdd = useCallback(
    (newItem) => {
      addFireSuppresantToBusiness({
        businessId,
        ...newItem,
        gas_type: newItem["gas_type.value"]
          ? newItem["gas_type.value"].value
          : null,
        gas_used: null,
        callback: fetchData,
        use_swal: true,
      });
    },
    [businessId, fetchData]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteFireSuppresant({
        businessId,
        objectId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [businessId, fetchData]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "invoice_date",
        header: t("INVOICE_DATE"),
        type: "text",
        canHide: true,
        canSort: true,
        placeholder: t("DD_MM_YYYY"),
        optional: true,
        width: "120px",
      },
      {
        accessor: "invoice_number",
        header: t("INVOICE_NO"),
        type: "text",
        canSort: true,
        canHide: true,
        optional: true,
        width: "120px",
      },
      {
        accessor: "nickname",
        header: t("NICKNAME"),
        type: "text",
        disableSortBy: true,
        placeholder: t("NAME"),
        width: "120px",
      },
      {
        accessor: "gas_type.value",
        header: t("SUPPRESANT_UNIT"),
        type: "dropdown",
        dropdownData: dropdownGasType,
        disableSortBy: true,
      },

      {
        accessor: "container_size",
        header: t("CONTAINER_SIZE"),
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "90px",
        tipHeader: "Tip",
        tipBody: t("CONTAINER_SIZE_TIP"),
      },
      {
        accessor: "mobility",
        header: t("MOBILITY"),
        type: "switch",
        disableSortBy: true,
        width: "100px",
        choice1: t("FIXED"),
        choice2: t("PORTABLE"),
      },
      {
        accessor: "leased",
        header: t("LEASED"),
        type: "switch",
        disableSortBy: true,
        width: "100px",
      },
      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "40px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "price_invoice",
        header: t("PRICE_INVOICE"),
        type: "number",
        canSort: true,
        canHide: true,
        placeholder: "0",
        optional: true,
        subtotalUnit: "DKK",
        tipHeader: "Tip",
        tipBody: t("PRICE_INVOICE_TIP"),
        width: "100px",
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "120px",
      },
    ],
    [dropdownGasType, t]
  );

  const disableRules = {
    carbon: () => true,
  };

  const subtotalColumns = {
    price_invoice: { label: t("TOTAL_PRICE") },
    carbon: { label: t("TOTAL_CARBON") },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      "gas_type.value": dropdownGasType,
    }),
    [dropdownGasType]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={{ carbon: CustomInputComponent }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
    />
  );
};

export default SuppresantTable;
